<template>
  <div>
    <PAutocomplete
      ref="mainElement"
      :model-value="modelValue"
      @update:model-value="updateModel"
      placeholder="Saisir le nom d'un destinataire (agent ou boite mél)"
      :values="options"
      :label="label || ''"
      :help="help"
      @search="search"
      :multiple="false"
      :same-width="false"
      data-sentry-id="SelectUser"
      :hide-messages="hideMessages"
      :error-messages="searchErrorMessages"
    >
      <template #option="{ option }">
        <div>
          <span class="italic" v-if="(option as any).type === 'CC'">Flux ({{ option.value.flux.length }}): </span>
          <span>{{ option.label }}</span>
        </div>
      </template>
    </PAutocomplete>
    <div aria-live="polite">
      <p class="sr-only">Nombre de suggestions: {{ options.length }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type Nullable, PAutocomplete, PastaNetworkError } from "@pasta/box-v2";
import { type AgentsDirectoryAutocompleteItem } from "../dto/agents-directory.dto";
import { computed, ref } from "vue";
import { useAgentsDirectoryStore } from "../stores/agents-directory.store";
import { debounce, get } from "lodash";
import { useLetterFileSavedFluxStore } from "../stores/letter-file-saved-flux.store";
import { type SavedFluxDto } from "../dto/saved-flux.dto";
import { isAutocompletable } from "../utils/agent-autocomplete";

interface Props {
  modelValue: Nullable<AgentsDirectoryAutocompleteItem[]>;
  label?: string;
  help?: string;
  withCC?: boolean;
  hideMessages?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  withCC: false,
  hideMessages: false,
});
const emit = defineEmits<(e: "update:modelValue", value: AgentsDirectoryAutocompleteItem[]) => void>();

const agentsDirectoryStore = useAgentsDirectoryStore();
const suggestions = ref<AgentsDirectoryAutocompleteItem[]>([]);
const searchErrorMessages = ref<string[]>([]);
const letterFileSavedFluxStore = useLetterFileSavedFluxStore();
const matchedSavedFluxItems = ref<{ label: string; value: SavedFluxDto; type: "CC" }[]>([]);

const options = computed<
  {
    label: string;
    type: "agent" | "CC";
    value: AgentsDirectoryAutocompleteItem | SavedFluxDto;
  }[]
>(() => {
  const agents: {
    label: string;
    value: AgentsDirectoryAutocompleteItem;
    type: "agent";
  }[] = suggestions.value.map(entry => ({
    label: getLabel(entry),
    value: entry,
    type: "agent",
  }));
  if (props.withCC) {
    return [...matchedSavedFluxItems.value, ...agents];
  }
  return agents;
});

function getLabel(entry: AgentsDirectoryAutocompleteItem | SavedFluxDto): string {
  if (get(entry, "flux")) {
    const val = entry as SavedFluxDto;
    return val.name;
  }
  const val = entry as AgentsDirectoryAutocompleteItem;
  return `${val.fullName} - ${val.mail}`;
}

const search = debounce(async (value: string) => {
  searchErrorMessages.value = [];
  if (!value) return;
  if (props.withCC) {
    matchedSavedFluxItems.value = letterFileSavedFluxStore.list.data
      .filter(item => {
        const val = value.toLowerCase();
        const itemName = item.name.toLowerCase();
        return val === "flux" || itemName.toLowerCase().includes(val);
      })
      .map(item => ({ label: item.name, value: item, type: "CC" }));
  }
  if (!isAutocompletable(value) || isSelectedLabel(value)) return;
  await fetchSuggestions(value);
}, 500);

function isSelectedLabel(value: string): boolean {
  const selected = props.modelValue?.at(0);
  return !!selected && value === getLabel(selected);
}

async function fetchSuggestions(value: string) {
  try {
    suggestions.value = await agentsDirectoryStore.autocomplete(value);
  } catch (error) {
    if (!(error instanceof PastaNetworkError) || error.statusCode !== 503) throw error;
    suggestions.value = [];
    searchErrorMessages.value = [
      "La recherche est momentanément indisponible, veuillez réessayer dans quelques instants.",
    ];
  }
}

function updateModel($event: AgentsDirectoryAutocompleteItem | SavedFluxDto) {
  if (get($event, "flux")) {
    emit(
      "update:modelValue",
      ($event as SavedFluxDto).flux.map(item => ({
        fullName: item.name,
        mail: item.mail,
      }))
    );
  } else {
    emit("update:modelValue", [$event as AgentsDirectoryAutocompleteItem]);
  }
}

const mainElement = ref<typeof PAutocomplete | null>(null);
function focus() {
  mainElement.value?.focus();
}
defineExpose({ mainElement, focus });
</script>
